import React from "react";
import { apiBatalPratinjau, apiBatalNota, formatApiErrorDetail } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Loader2, Ban, AlertTriangle } from "lucide-react";

// Owner only (the server rejects everyone else). Shows what will happen
// before cancelling: payment so far, each SPK's production position, warnings.
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export default function BatalNotaForm({ notaId, onDone, onCancel }) {
  const [prev, setPrev] = React.useState(null);
  const [alasan, setAlasan] = React.useState("");
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    apiBatalPratinjau(notaId).then(setPrev).catch((e) => setError(errText(e)));
  }, [notaId]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { onDone(await apiBatalNota(notaId, alasan.trim())); }
    catch (e2) { setError(errText(e2)); } finally { setBusy(false); }
  };

  if (!prev && !error) return <div className="p-3 text-sm text-slate-400"><Loader2 className="inline animate-spin mr-2" size={14} />Memuat pratinjau…</div>;
  return (
    <form data-testid="batal-form" onSubmit={submit} className="rounded-lg border border-red-500/40 bg-red-500/5 p-4 space-y-3">
      <div className="text-sm font-semibold text-red-300">Batalkan nota {prev?.nomor_nota}</div>
      {prev && (<>
        <div className="text-sm text-slate-300">
          Total <b>{rupiah(prev.total)}</b> · Sudah dibayar <b data-testid="batal-dibayar">{rupiah(prev.dibayar)}</b> · Status {prev.status_bayar.replace("_", " ")}
        </div>
        <ul className="text-xs text-slate-400 space-y-0.5" data-testid="batal-spk">
          {prev.spk.map((s) => (
            <li key={s.spk_id}><span className="font-mono text-slate-300">{s.nomor_spk}</span> — {s.status_produksi || "Belum masuk produksi"}
              {s.langkah_aktif.length > 0 && ` (sedang dikerjakan: ${s.langkah_aktif.join(", ")})`}</li>
          ))}
        </ul>
        {prev.peringatan.map((w, i) => (
          <div key={i} data-testid={`batal-peringatan-${i}`} className="flex items-start gap-2 text-xs text-amber-300"><AlertTriangle size={14} className="shrink-0 mt-0.5" />{w}</div>
        ))}
        <p className="text-xs text-slate-500">Nota, SPK, dan pembayaran tidak dihapus. Semua SPK berhenti dari antrean produksi. Nomor faktur tidak dipakai ulang.</p>
        <div>
          <label className="text-[13px] font-semibold text-slate-200">Alasan pembatalan (wajib)</label>
          <textarea data-testid="batal-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)} required minLength={5} maxLength={300} rows={2}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-red-500/60" />
        </div>
      </>)}
      {error && <p data-testid="batal-error" className="text-xs text-red-300">{error}</p>}
      <div className="flex gap-2">
        {prev && <button data-testid="batal-submit" type="submit" disabled={busy || alasan.trim().length < 5}
          className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Ban size={15} />{busy ? "Memproses…" : "Ya, batalkan nota"}</button>}
        <button type="button" data-testid="batal-tutup" onClick={onCancel} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300">Tutup</button>
      </div>
    </form>
  );
}
